import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, Plus, Trash2, Ban, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatBs, formatTime } from "@/lib/format";

export default function CuposPage() {
  const qc = useQueryClient();
  const [sorteoId, setSorteoId] = useState("");
  const [number, setNumber] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const { data: lotteries = [] } = useQuery({
    queryKey: ["lottery-types", "all"],
    queryFn: async () => (await api.get("/lottery-types")).data,
  });
  const { data: sorteos = [] } = useQuery({
    queryKey: ["sorteos", "available-cupo"],
    queryFn: async () => (await api.get("/sorteos/available")).data,
  });
  const { data: cupos = [] } = useQuery({
    queryKey: ["cupos", sorteoId],
    queryFn: async () => (await api.get(`/cupos?sorteo_id=${sorteoId}`)).data,
    enabled: !!sorteoId,
  });

  const lotteryMap = useMemo(() => Object.fromEntries(lotteries.map((l) => [l.id, l])), [lotteries]);
  const sorteoMap = useMemo(() => Object.fromEntries(sorteos.map((s) => [s.id, s])), [sorteos]);
  const selectedSorteo = sorteoMap[sorteoId];
  const selectedLottery = selectedSorteo ? lotteryMap[selectedSorteo.lottery_type_id] : null;

  const create = useMutation({
    mutationFn: async () => (await api.post("/cupos", {
      lottery_type_id: selectedSorteo.lottery_type_id,
      sorteo_id: sorteoId,
      number,
      max_amount_bs: parseFloat(maxAmount) || 0,
    })).data,
    onSuccess: () => { toast.success("Cupo creado"); setNumber(""); setMaxAmount(""); qc.invalidateQueries({ queryKey: ["cupos", sorteoId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al crear cupo"),
  });

  const patch = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/cupos/${id}`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cupos", sorteoId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/cupos/${id}`)).data,
    onSuccess: () => { toast.success("Cupo eliminado"); qc.invalidateQueries({ queryKey: ["cupos", sorteoId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Package size={24} className="text-primary" /> Cupos / Disponibilidad
        </h1>
        <p className="text-sm text-muted-foreground">Define el cupo máximo (Bs) por número y sorteo. Si un número no tiene cupo, se considera disponibilidad ilimitada.</p>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Sorteo</Label>
            <Select value={sorteoId} onValueChange={(v) => setSorteoId(v)}>
              <SelectTrigger data-testid="cupos-sorteo-select"><SelectValue placeholder="Selecciona un sorteo abierto" /></SelectTrigger>
              <SelectContent>
                {sorteos.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {(lotteryMap[s.lottery_type_id]?.name || "Lotería")} · {formatTime(s.scheduled_at)} {s.label ? `(${s.label})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Número {selectedLottery ? `(${selectedLottery.number_length}d)` : ""}</Label>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Ej. 05" disabled={!sorteoId} data-testid="cupos-number-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Cupo máx (Bs)</Label>
            <div className="flex gap-2">
              <Input type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="Ej. 5000" disabled={!sorteoId} data-testid="cupos-max-input" />
              <Button onClick={() => create.mutate()} disabled={!sorteoId || !number || !maxAmount || create.isPending} data-testid="cupos-add-button"><Plus size={16} /></Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-card border-border">
        <div className="text-sm font-display font-semibold mb-3">
          Cupos {selectedSorteo ? `· ${selectedLottery?.name} ${formatTime(selectedSorteo.scheduled_at)}` : ""}
        </div>
        {!sorteoId && <div className="py-8 text-center text-sm text-muted-foreground">Selecciona un sorteo para ver y gestionar sus cupos.</div>}
        {sorteoId && cupos.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Sin cupos para este sorteo (disponibilidad ilimitada).</div>}
        <div className="space-y-2">
          {cupos.map((c) => {
            const remaining = Math.max(0, c.max_amount_bs - c.sold_amount_bs);
            const pct = c.max_amount_bs > 0 ? Math.min(100, (c.sold_amount_bs / c.max_amount_bs) * 100) : 0;
            const blocked = c.status !== "active";
            const soldOut = remaining <= 0;
            return (
              <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2" data-testid={`cupos-row-${c.number}`}>
                <div className="h-9 w-12 grid place-items-center rounded-md font-mono text-lg font-bold border border-border">{c.number}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Vendido <span className="font-mono text-foreground">Bs {formatBs(c.sold_amount_bs)}</span> / <span className="font-mono">Bs {formatBs(c.max_amount_bs)}</span></span>
                    {blocked ? <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/40 text-[10px]">BLOQUEADO</Badge>
                      : soldOut ? <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px]">AGOTADO</Badge>
                      : <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px]">Disp. Bs {formatBs(remaining)}</Badge>}
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <Button size="icon" variant="ghost" title={blocked ? "Reactivar" : "Bloquear"} onClick={() => patch.mutate({ id: c.id, body: { status: blocked ? "active" : "blocked" } })} data-testid={`cupos-toggle-${c.number}`}>
                  {blocked ? <CheckCircle2 size={16} className="text-emerald-300" /> : <Ban size={16} className="text-amber-300" />}
                </Button>
                <Button size="icon" variant="ghost" className="text-rose-300" onClick={() => del.mutate(c.id)} data-testid={`cupos-delete-${c.number}`}><Trash2 size={16} /></Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
